// Canonical brand registry + normalisation for brand fit matching.
//
// IMPORTANT: This file MUST stay byte-identical to
// `supabase/functions/_shared/brandRegistry.ts`. Edge functions cannot import
// from `src/lib`, so the module is duplicated. If you change one, change both.

export interface BrandEntry {
  canonical: string;
  aliases: string[];
  domain_patterns: string[];
}

export interface MultiBrandRetailer {
  name: string;
  domain: string;
}

// Department-store / marketplace retailers that sell many brands.
// Products scraped from these domains should have the actual product brand
// extracted separately (e.g. via LLM) rather than bucketed under the retailer.
export const MULTI_BRAND_RETAILERS: MultiBrandRetailer[] = [
  { name: "Selfridges", domain: "selfridges.com" },
  { name: "John Lewis", domain: "johnlewis.com" },
  { name: "Amazon", domain: "amazon.co.uk" },
  { name: "Amazon", domain: "amazon.com" },
  { name: "Lyst", domain: "lyst.com" },
  { name: "Zalando", domain: "zalando.co.uk" },
  { name: "Zalando", domain: "zalando.com" },
  { name: "Net-a-Porter", domain: "net-a-porter.com" },
  { name: "Farfetch", domain: "farfetch.com" },
  { name: "MyTheresa", domain: "mytheresa.com" },
  { name: "The Outnet", domain: "theoutnet.com" },
  { name: "MatchesFashion", domain: "matchesfashion.com" },
  { name: "Mr Porter", domain: "mrporter.com" },
  { name: "ASOS Marketplace", domain: "marketplace.asos.com" },
  { name: "Not On The High Street", domain: "notonthehighstreet.com" },
  { name: "Etsy", domain: "etsy.com" },
];

export const BRAND_REGISTRY: BrandEntry[] = [
  {
    canonical: "Zara",
    aliases: ["Zara", "ZARA", "Zara Home", "Zara Man", "Zara Woman", "zara"],
    domain_patterns: ["zara.com", "zara."],
  },
  {
    canonical: "ASOS",
    aliases: ["ASOS", "ASOS Design", "ASOS DESIGN", "Asos", "Asos Design", "asos"],
    domain_patterns: ["asos.com", "asos."],
  },
  {
    canonical: "H&M",
    aliases: ["H&M", "HM", "H & M", "H and M", "Hennes & Mauritz", "H&M Man", "H&M Woman", "hm"],
    domain_patterns: ["hm.com", "www2.hm.com", "hm."],
  },
  {
    canonical: "COS",
    aliases: ["COS", "Cos", "cos"],
    domain_patterns: ["cos.com", "cosstores.com"],
  },
  {
    canonical: "& Other Stories",
    aliases: ["& Other Stories", "Other Stories", "And Other Stories", "Stories", "other_stories"],
    domain_patterns: ["stories.com", "andotherstories.com"],
  },
  {
    canonical: "Next",
    aliases: ["Next", "Next UK", "NEXT", "next"],
    domain_patterns: ["next.co.uk", "next.com"],
  },
  {
    canonical: "Mango",
    aliases: ["Mango", "MANGO", "Mango Man", "Mango Woman", "mango"],
    domain_patterns: ["mango.com", "shop.mango.com"],
  },
  {
    canonical: "Arket",
    aliases: ["Arket", "ARKET", "arket"],
    domain_patterns: ["arket.com"],
  },
  {
    canonical: "Uniqlo",
    aliases: ["Uniqlo", "UNIQLO", "uniqlo"],
    domain_patterns: ["uniqlo.com"],
  },
  {
    canonical: "Weekday",
    aliases: ["Weekday", "WEEKDAY", "weekday"],
    domain_patterns: ["weekday.com"],
  },
  {
    canonical: "Sezane",
    aliases: ["Sezane", "Sézane", "SEZANE", "sezane"],
    domain_patterns: ["sezane.com"],
  },
  {
    canonical: "Massimo Dutti",
    aliases: ["Massimo Dutti", "MASSIMO DUTTI", "Massimo", "massimo_dutti", "massimodutti"],
    domain_patterns: ["massimodutti.com"],
  },
  {
    canonical: "Topshop",
    aliases: ["Topshop", "TOPSHOP", "Top Shop", "topshop"],
    domain_patterns: ["topshop.com"],
  },
  {
    canonical: "Topman",
    aliases: ["Topman", "TOPMAN", "Top Man", "topman"],
    domain_patterns: ["topman.com"],
  },
  {
    canonical: "New Look",
    aliases: ["New Look", "NEW LOOK", "Newlook", "NewLook", "new_look", "newlook"],
    domain_patterns: ["newlook.com"],
  },
  {
    canonical: "Miss Selfridge",
    aliases: ["Miss Selfridge", "MISS SELFRIDGE", "miss_selfridge", "missselfridge"],
    domain_patterns: ["missselfridge.com"],
  },
  {
    canonical: "River Island",
    aliases: ["River Island", "RIVER ISLAND", "RiverIsland", "river_island", "riverisland"],
    domain_patterns: ["riverisland.com"],
  },
  {
    canonical: "Bershka",
    aliases: ["Bershka", "BERSHKA", "bershka"],
    domain_patterns: ["bershka.com"],
  },
  {
    canonical: "Pull & Bear",
    aliases: ["Pull & Bear", "Pull and Bear", "PullAndBear", "Pull&Bear", "PULL&BEAR", "pull_and_bear", "pullandbear"],
    domain_patterns: ["pullandbear.com"],
  },
  {
    canonical: "Stradivarius",
    aliases: ["Stradivarius", "STRADIVARIUS", "stradivarius"],
    domain_patterns: ["stradivarius.com"],
  },
  {
    canonical: "Vero Moda",
    aliases: ["Vero Moda", "VERO MODA", "VeroModa", "vero_moda", "veromoda"],
    domain_patterns: ["veromoda.com"],
  },
  {
    canonical: "M&S",
    aliases: ["M&S", "Marks & Spencer", "Marks and Spencer", "Marks&Spencer", "MS", "ms", "marks_and_spencer", "marksandspencer"],
    domain_patterns: ["marksandspencer.com", "marksandspencer."],
  },
  {
    canonical: "John Lewis",
    aliases: ["John Lewis", "JOHN LEWIS", "John Lewis & Partners", "JohnLewis", "john_lewis", "johnlewis"],
    domain_patterns: ["johnlewis.com"],
  },
  {
    canonical: "White Stuff",
    aliases: ["White Stuff", "WHITE STUFF", "WhiteStuff", "white_stuff", "whitestuff"],
    domain_patterns: ["whitestuff.com"],
  },
  {
    canonical: "FatFace",
    aliases: ["FatFace", "Fat Face", "FAT FACE", "FATFACE", "fat_face", "fatface"],
    domain_patterns: ["fatface.com"],
  },
  {
    canonical: "Sandro",
    aliases: ["Sandro", "SANDRO", "Sandro Paris", "sandro"],
    domain_patterns: ["sandro-paris.com", "sandro."],
  },
  {
    canonical: "Jack & Jones",
    aliases: ["Jack & Jones", "Jack and Jones", "JackJones", "JACK & JONES", "jack_and_jones", "jackjones"],
    domain_patterns: ["jackjones.com"],
  },
  {
    canonical: "PrettyLittleThing",
    aliases: ["PrettyLittleThing", "Pretty Little Thing", "PLT", "plt", "prettylittlething"],
    domain_patterns: ["prettylittlething.com"],
  },
  {
    canonical: "Missguided",
    aliases: ["Missguided", "MISSGUIDED", "Miss Guided", "missguided"],
    domain_patterns: ["missguided.com", "missguided.co.uk"],
  },
  {
    canonical: "Boohoo",
    aliases: ["Boohoo", "BOOHOO", "boohoo.com", "boohoo"],
    domain_patterns: ["boohoo.com"],
  },
  {
    canonical: "Reiss",
    aliases: ["Reiss", "REISS", "reiss"],
    domain_patterns: ["reiss.com"],
  },
  {
    canonical: "AllSaints",
    aliases: ["AllSaints", "All Saints", "ALLSAINTS", "all_saints", "allsaints"],
    domain_patterns: ["allsaints.com"],
  },
  {
    canonical: "Ted Baker",
    aliases: ["Ted Baker", "TED BAKER", "TedBaker", "ted_baker", "tedbaker"],
    domain_patterns: ["tedbaker.com"],
  },
  {
    canonical: "Whistles",
    aliases: ["Whistles", "WHISTLES", "whistles"],
    domain_patterns: ["whistles.com"],
  },
  {
    canonical: "Hush",
    aliases: ["Hush", "HUSH", "Hush Homewear", "hush"],
    domain_patterns: ["hush-uk.com"],
  },
  {
    canonical: "Anthropologie",
    aliases: ["Anthropologie", "ANTHROPOLOGIE", "Anthro", "anthropologie"],
    domain_patterns: ["anthropologie.com"],
  },
  {
    canonical: "Levi's",
    aliases: ["Levi's", "Levis", "LEVI'S", "LEVIS", "Levi Strauss", "levis", "levi"],
    domain_patterns: ["levi.com", "levis."],
  },
  {
    canonical: "Gap",
    aliases: ["Gap", "GAP", "gap"],
    domain_patterns: ["gap.com", "gap.co.uk"],
  },
  {
    canonical: "Nike",
    aliases: ["Nike", "NIKE", "nike"],
    domain_patterns: ["nike.com"],
  },
  {
    canonical: "Adidas",
    aliases: ["adidas", "Adidas", "ADIDAS"],
    domain_patterns: ["adidas.com", "adidas.co.uk"],
  },
  {
    canonical: "Primark",
    aliases: ["Primark", "PRIMARK", "primark"],
    domain_patterns: ["primark.com"],
  },
  {
    canonical: "Gymshark",
    aliases: ["Gymshark", "GYMSHARK", "gymshark"],
    domain_patterns: ["gymshark.com"],
  },
  {
    canonical: "Clarks",
    aliases: ["Clarks", "CLARKS", "clarks"],
    domain_patterns: ["clarks.com", "clarks.co.uk"],
  },
];

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function normaliseBrand(input: string | null | undefined): string | null {
  if (!input) return null;

  const trimmed = input.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();

  // Try exact alias match first
  for (const entry of BRAND_REGISTRY) {
    if (entry.aliases.some((alias) => alias.toLowerCase() === lower)) {
      return entry.canonical;
    }
  }

  // Try domain pattern match
  for (const entry of BRAND_REGISTRY) {
    if (entry.domain_patterns.some((pattern) => lower.includes(pattern.toLowerCase()))) {
      return entry.canonical;
    }
  }

  // Try word-boundary alias match (whole word only — avoids "ms" matching
  // inside "gymshark" or "stories" matching inside "histories").
  for (const entry of BRAND_REGISTRY) {
    if (
      entry.aliases.some((alias) => {
        const aliasLower = alias.toLowerCase();
        // Only attempt word-boundary match for aliases with at least one
        // word character at each end (regex \b is meaningful there).
        if (!/^\w/.test(aliasLower) || !/\w$/.test(aliasLower)) return false;
        const wordPattern = new RegExp(`\\b${escapeRegex(aliasLower)}\\b`, "i");
        return wordPattern.test(trimmed);
      })
    ) {
      return entry.canonical;
    }
  }

  // No match — return the original trimmed input for graceful degradation
  return trimmed;
}

// ============================================================================
// RETAILER-CAP-1 — Demoted retailers (Phase 1)
// ============================================================================

/**
 * Generic marketplaces with low fit-data confidence and fast-fashion / random-
 * seller noise. These domains never fill MID or PREMIUM tier chips in
 * Text / Screenshot / Social flows. BUDGET tier still allows them.
 *
 * URL flow: user-pasted items from these retailers are preserved at their
 * natural price tier by the URL-pin path (separate code path that overrides
 * mid after partitionTiers — see analyse-outfit/index.ts).
 *
 * Curation rule, not values rule — Shein/Romwe etc. are intentionally
 * excluded; see SHEIN-ROMWE-DECISION backlog.
 */
export const DEMOTED_RETAILER_DOMAINS: readonly string[] = [
  // Amazon TLDs
  "amazon.com",
  "amazon.co.uk",
  "amazon.de",
  "amazon.fr",
  "amazon.it",
  "amazon.es",
  // eBay TLDs
  "ebay.com",
  "ebay.co.uk",
  "ebay.de",
  // Other marketplaces
  "aliexpress.com",
  "aliexpress.us",
  "temu.com",
  "walmart.com",
  "alibaba.com",
  "wish.com",
];

/**
 * Returns true if the given URL belongs to a demoted retailer (host-only
 * check, www-stripped, case-insensitive). Invalid/empty inputs return false.
 */
export function isDemotedRetailer(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    return DEMOTED_RETAILER_DOMAINS.includes(host);
  } catch {
    return false;
  }
}
